/**
 * Policy record — Facilities tab (OR-1665, design OR-1639 "MVP").
 *
 * Columns per the approved MVP: Facility name, Address (full, no separate
 * State column), Status, Premium, Inception, Divested. No tech fee and no
 * facility type for the MVP.
 *
 * States, each from its own design frame:
 *  - search with no match → "No Results Found / Try a different search"
 *  - a bound policy with no facilities → explains it and links to the
 *    submission (new tab) so the user can fix it there
 *  - the request failed → "Couldn't load facilities" with Try again,
 *    which re-requests the rows
 *
 * Rows come from `GET /policy-terms/{id}/facilities/` (backend OR-1620);
 * search, sort and paging run on the server.
 *
 * Status: the backend tracks each row as `active` or `removed` (its
 * coverage ended). An active row reads the term's own status, a removed one
 * reads Cancelled, so the column keeps the design's labels.
 */
import { useEffect, useRef, useState } from "react";
import { Building07, LinkExternal01, RefreshCw05 } from "@untitledui/icons";
import { Button, Table } from "@tricura/atlas-ui";
import { ListEmptyState } from "../../components/list-states";
import { PaginationFooter } from "../../components/pagination-footer";
import { Skeleton } from "../../components/skeleton";
import { PlainHeader, SortHeader, parseSort } from "../../components/table-sort-header";
import { type PolicyTermStatus, type TermFacility, type TermFacilitySort, listTermFacilities } from "../../api";
import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { PolicyStatusBadge } from "../policies-list/status-badge";
import { formatDate, moneyCompact } from "./format";
import { TabState } from "./tab-state";

const DEFAULT_PAGE_SIZE = 10;

interface Column {
    id: "facility_name" | "address" | "status" | "premium" | "inception" | "divested";
    label: string;
    width: number;
    sortable: boolean;
    align?: "right";
}

const COLUMNS: Column[] = [
    { id: "facility_name", label: "Facility name", width: 240, sortable: true },
    { id: "address", label: "Address", width: 300, sortable: true },
    { id: "status", label: "Status", width: 190, sortable: false },
    { id: "premium", label: "Premium", width: 110, sortable: true, align: "right" },
    { id: "inception", label: "Inception", width: 130, sortable: true },
    { id: "divested", label: "Divested", width: 130, sortable: true },
];

/** Column id → the backend's sort field. */
const SORT_FIELD: Record<string, TermFacilitySort> = {
    facility_name: "location_name",
    address: "address",
    premium: "premium",
    inception: "effective_from",
    divested: "effective_to",
};

function toBackendSort(sort: string | undefined): string | undefined {
    const parsed = parseSort(sort);
    if (!parsed) return undefined;
    const field = SORT_FIELD[parsed.field];
    return field && (parsed.dir === "desc" ? `-${field}` : field);
}

type Load = { status: "loading" } | { status: "ready"; rows: TermFacility[]; total: number } | { status: "error" };

interface FacilitiesTabProps {
    termId: string;
    /** What an active coverage row reads in the Status column. */
    termStatus: PolicyTermStatus;
    search: string;
    /** Opens the submission the term was bound from; null hides the link. */
    submissionHref: string | null;
}

export function FacilitiesTab({ termId, termStatus, search, submissionHref }: FacilitiesTabProps) {
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    const [sort, setSort] = useState<string | undefined>(undefined);
    const [offset, setOffset] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const debouncedSearch = useDebouncedValue(search.trim(), 300);

    // A new search or term starts from the first page.
    useEffect(() => setOffset(0), [debouncedSearch, termId]);

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        listTermFacilities(termId, { offset, limit: pageSize, search: debouncedSearch || undefined, sort: toBackendSort(sort) })
            .then((res) => !cancelled && setLoad({ status: "ready", rows: res.items, total: res.total }))
            .catch(() => !cancelled && setLoad({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termId, offset, pageSize, debouncedSearch, sort, reloadKey]);

    const page = load.status === "ready" ? load.rows : [];
    const total = load.status === "ready" ? load.total : 0;
    const noRows = load.status === "ready" && total === 0;

    // react-aria's table doesn't spread spare width over fixed-width columns,
    // so measure the scroll wrapper and stretch them proportionally (same
    // approach as the Triage and Policies tables). Below the declared total
    // the table keeps its widths and scrolls.
    const totalWidth = COLUMNS.reduce((sum, c) => sum + c.width, 0);
    const tableRef = useRef<HTMLTableElement>(null);
    const [availWidth, setAvailWidth] = useState(0);
    const tableMounted = load.status !== "error" && !noRows;
    useEffect(() => {
        const wrapper = tableRef.current?.parentElement;
        if (!wrapper) return;
        const ro = new ResizeObserver(([entry]) => entry && setAvailWidth(entry.contentRect.width));
        ro.observe(wrapper);
        setAvailWidth(wrapper.clientWidth);
        return () => ro.disconnect();
    }, [tableMounted]);
    const widthOf = (c: Column) => {
        const slack = Math.max(0, availWidth - totalWidth);
        return c.width + (slack * c.width) / totalWidth;
    };

    if (load.status === "error") {
        return (
            <TabState
                icon={Building07}
                title="Couldn't load facilities"
                description="We had some trouble loading the facilities for this policy."
                data-autotest="policy-facilities-state-error"
                action={
                    <Button color="secondary" size="sm" iconTrailing={RefreshCw05} onClick={() => setReloadKey((k) => k + 1)}>
                        Try again
                    </Button>
                }
            />
        );
    }

    if (noRows && !debouncedSearch) {
        return (
            <TabState
                icon={Building07}
                title="No Facilities found"
                description="Bound policies should always include at least one facility. Review the submission to correct this."
                data-autotest="policy-facilities-state-empty"
                action={
                    submissionHref && (
                        <Button color="secondary" size="sm" iconTrailing={LinkExternal01} href={submissionHref} target="_blank" rel="noopener noreferrer">
                            View Submission
                        </Button>
                    )
                }
            />
        );
    }

    if (noRows) {
        return <ListEmptyState description="Try a different search" />;
    }

    const skeletonItems = Array.from({ length: 6 }, (_, i) => ({ id: `skeleton-${i}` }));

    return (
        <>
            <Table
                ref={tableRef}
                aria-label="Facilities"
                data-autotest="policy-facilities-table"
                aria-busy={load.status === "loading" || undefined}
                className="table-fixed"
                wrapperClassName="atlas-scrollbar flex-1 min-h-0 overflow-auto"
                style={{ minWidth: totalWidth }}
            >
                <Table.Header className="bg-secondary sticky top-0 z-20">
                    {COLUMNS.map((c, i) => (
                        <Table.Head key={c.id} id={c.id} isRowHeader={i === 0} style={{ width: widthOf(c), minWidth: widthOf(c) }} className={c.align === "right" ? "text-right" : undefined}>
                            {c.sortable ? (
                                <SortHeader field={c.id} sort={sort} onSortChange={(next) => (setOffset(0), setSort(next))}>
                                    {c.label}
                                </SortHeader>
                            ) : (
                                <PlainHeader>{c.label}</PlainHeader>
                            )}
                        </Table.Head>
                    ))}
                </Table.Header>
                {load.status === "loading" ? (
                    <Table.Body items={skeletonItems}>
                        {(item) => (
                            <Table.Row id={item.id}>
                                {COLUMNS.map((c) => (
                                    <Table.Cell key={c.id}>
                                        <Skeleton className="h-4 w-20" />
                                    </Table.Cell>
                                ))}
                            </Table.Row>
                        )}
                    </Table.Body>
                ) : (
                    <Table.Body items={page}>
                        {(row) => (
                            <Table.Row id={row.id} data-autotest="policy-facilities-row">
                                <Table.Cell>
                                    <span className="text-secondary block truncate text-sm" title={row.facilityName}>
                                        {row.facilityName}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-secondary block truncate text-sm" title={row.address}>
                                        {row.address}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <PolicyStatusBadge status={row.status === "removed" ? "cancelled" : termStatus} />
                                </Table.Cell>
                                <Table.Cell className="text-right">
                                    <span className="text-secondary text-sm tabular-nums">{moneyCompact(row.premium)}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-secondary text-sm tabular-nums">{formatDate(row.inceptionDate)}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-secondary text-sm tabular-nums">{formatDate(row.divestedDate)}</span>
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                )}
            </Table>
            <PaginationFooter
                total={total}
                offset={offset}
                limit={pageSize}
                onChange={setOffset}
                onLimitChange={setPageSize}
                isDisabled={load.status === "loading"}
            />
        </>
    );
}
